import React, { Reducer, useEffect, useReducer } from 'react';
import { text } from '../../data';
import TableLoader from '../TableLoader';
import SealModal from './SealModal';
import { generateId } from '../../utils/engineering-tools';
import { View } from '../../types/types';
import SearchPagination from '../../../components/bearing-search/components/SearchPagination';
import Loader from '../../../components/bearing-search/components/Loader';
import productSearchReducer, {
	initialSearchState,
	ProductSearchAction,
	ProductSearchState,
} from './reducer';
import SearchPaginationLabel from '../../../components/bearing-search/components/SearchPaginationLabel';
import {
	analyticsData,
	getSealImpressionData,
	trackSealListImpression,
} from '../../utils/analytics';
import { trackProductClick } from '../../../analytics/engineering-tools';
import SortButton from '../SortButton';
import {
	SealDataKey,
	SealSortPayload,
} from '../../../components/bearing-search/types/types';

import {
	getSealByPartNumber
} from '../../../components/bearing-search/api/api';

interface SealSearchResultsProps {
	data: Array<Array<string>>;
	columnKeys: string[];
	columnLabels: Record<string, string>;
	unit: string;
	refining: boolean;
	loading?: boolean;
	onSort?: ( option?: SealSortPayload ) => void;
	partNumber?: string
	keyFieldId?: number;
	urlFieldId?: number;
	drawFieldId?: number;
	urlComposer?: ( partNumber: string, unit: string ) => string;
	drawUrlComposer?: ( drawNum: string) => string;
}

const SealSearchResults: React.FC<SealSearchResultsProps> = ( {
	data,
	columnKeys,
	columnLabels,
	unit,
	loading,
	refining,
	keyFieldId = -1,
	urlFieldId = 0,
	drawFieldId = 3,
	urlComposer,
	drawUrlComposer,
	onSort,
	partNumber
} ) => {
	const [ refinementState, dispatch ] = useReducer<
		Reducer<ProductSearchState, ProductSearchAction>
	>( productSearchReducer, initialSearchState );

	useEffect( () => {
		dispatch( {
			type: 'update_columns',
			payload: columnKeys,
		} );
	}, [ columnKeys ] );

	useEffect( () => {
		dispatch( {
			type: 'update_data',
			payload: [ data, refining ],
		} );
	}, [ data ] );

	useEffect( () => {
		const { currentPage, pageSize } = refinementState;
		trackSealListImpression(
			refinementState.displayedData,
			columnKeys,
			currentPage,
			pageSize,
			analyticsData.sealSearch.name,
		);
	}, [ refinementState.displayedData ] );

	const sort = ( sortKey: string ) => {
		if ( onSort ) {
			const sortDirection = sortKey !== refinementState.sortExpression || refinementState.sortDirection !== 'asc' ? 'asc' : 'desc';

			dispatch( {
				type: 'sort_by',
				payload: sortKey,
			} );

			onSort( {
				sortDirection,
				sortExpression: sortKey as SealDataKey,
			} );
		}
	};

	const handleProductClick = ( item: string[], index: number, detailsUrl: string ) => {

		if (item.length === 4) {
			const partNumber = {
				partNumber: item[2]
			}

			dispatch( {
				type: 'open_modal',
				payload: "true",
			} );

			getSealByPartNumber( partNumber )
				.then( ( response ) => {
					if (response.header.errorCount) {
						dispatch( {
							type: 'open_modal',
							payload: item[2],
						} );

					} else {
						dispatch( {
							type: 'open_modal',
							payload: "",
						} );

						window.open(detailsUrl,'_blank');
					}
				});
		} else {
			window.open(detailsUrl,'_blank');
		}

		const { currentPage, pageSize } = refinementState;

		const product = getSealImpressionData(
			item,
			columnKeys,
			index,
			currentPage,
			pageSize,
			analyticsData.sealSearch.name,
		);

		trackProductClick( product );
	};

	const closeSealModal = (close) => {
		if (close) {
			dispatch( {
				type: 'open_modal',
				payload: "",
			} );
		}
	}

	return (
		<div className="t-engineering-tools__results">

			{refinementState.partNumber && <SealModal partNumber={refinementState.partNumber} onClose={() => closeSealModal( true )} />}

			<h2>{text.results}</h2>

			<p className="t-engineering-tools__results-link d-none">
				<a href="#">{text.disclaimer}</a>
			</p>

			{loading && <TableLoader />}

			{! loading && (
				<>
					{( data.length === 0 || refinementState.filteredData.length === 0 ) && (
						<div className="t-engineering-tools__no-results">
							{text.noResults}
						</div>
					)}

					{data.length > 0 && (
						<>
							{refinementState.filteredData.length > 0 && (
								<div className="t-engineering-tools__view-bar">
									<SearchPaginationLabel
										current={refinementState.currentPage}
										total={refinementState.filteredData.length}
										pageSize={refinementState.pageSize}
									/>

									<div className="t-engineering-tools__view-toggle">
										<button className="a-icon-cta">
											<span
												className={
													refinementState.view === View.LIST
														? 'icon-list-fill'
														: 'icon-list-outline'
												}
												aria-label={text.list}
												onClick={() =>
													dispatch( { type: 'change_view', payload: View.LIST } )
												}
											></span>
										</button>

										<button className="a-icon-cta">
											<span
												className={
													refinementState.view === View.GRID
														? 'icon-grid-fill'
														: 'icon-grid-outline'
												}
												aria-label={text.grid}
												onClick={() =>
													dispatch( { type: 'change_view', payload: View.GRID } )
												}
											></span>
										</button>
									</div>
								</div>
							)}

							{refinementState.filteredData.length > 1 && refinementState.view === View.LIST && (
								<p className="t-engineering-tools__notes">
									{text.filterResultsNote}
								</p>
							)}

							{refinementState.view === View.LIST && (
								<div className="t-engineering-tools__table">
									{refinementState.isRefining && (
										<div className="t-engineering-tools__table-overlay">
											<Loader />
										</div>
									)}

									<div className="c-bearings-table">
										{refinementState.isRefining && (
											<div className="c-bearings-table__overlay">
												<Loader />
											</div>
										)}

										<table>
											<caption className="sr-only">{text.results}</caption>
											<thead>
												<tr>
													{columnKeys.map( ( columnKey, colIndex ) => {
														const label = columnLabels[ columnKey ];
														const { sortExpression, sortDirection } =
															refinementState;

														return (
															<th
																scope="col"
																key={`seal_search_column_head_${ columnKey }`}
															>
																<div>
																	{label}

																	{!! onSort &&
																		refinementState.filteredData.length > 1 && (
																		<SortButton
																			sortKey={columnKey}
																			direction={
																				columnKey === sortExpression
																					? sortDirection
																					: undefined
																			}
																			isActive={columnKey === sortExpression}
																			onSort={( newSortKey ) =>
																				sort( newSortKey )
																			}
																		/>
																	)}
																</div>

																<div className="mb-0 m-input m-search__search-part-number-field">
																	<label className="sr-only">
																		{columnLabels[ columnKey ]}
																	</label>
																	<input
																		className="m-input__input"
																		type="text"
																		placeholder={text.search}
																		value={
																			refinementState.filters[ colIndex ] || ''
																		}
																		onChange={(
																			event: React.ChangeEvent<HTMLInputElement>,
																		) =>
																			dispatch( {
																				type: 'filter_by',
																				payload: [
																					colIndex,
																					event.target.value as string,
																				],
																			} )
																		}
																	/>
																</div>
															</th>
														);
													} )}
												</tr>
											</thead>
											<tbody>
												{refinementState.displayedData.map( ( row, rowIndex ) => {
													const rowKey = generateId(
														`${ row[ keyFieldId ] ?? rowIndex }`,
													);
													let detailsUrl = '';
													let drawUrl = '';

													if ( row[ urlFieldId ] && urlComposer ) {
														detailsUrl = urlComposer( row[ urlFieldId ], unit );
													}

													if ( row[ drawFieldId ] && drawUrlComposer ) {
														drawUrl = drawUrlComposer( row[ drawFieldId ]);
													}

													return (
														<tr key={`seal_search_row_${ rowKey }`}>
															{row.map( ( column, colIndex ) => {
																const colKey = generateId(
																	`${ columnKeys[ colIndex ] || colIndex }`,
																);

																if ( colIndex === urlFieldId && detailsUrl ) {
																	return (
																		<td
																			key={`seal_search_data_${ rowKey }_${ colKey }`}
																		>
																			<a
																				className="btn btn--tertiary"
																				href={detailsUrl}
																				target="_blank"
																				rel="noreferrer"
																				onClick={(e) => {
																						e.preventDefault();
																						handleProductClick( row, rowIndex, detailsUrl )
																					}
																				}
																			>
																				{column}
																			</a>
																		</td>
																	);
																}

																if ( colIndex === drawFieldId && drawUrl ) {
																	return (
																		<td
																			key={`seal_search_data_${ rowKey }_${ colKey }`}
																		>
																			<a
																				className="btn btn--tertiary"
																				href={drawUrl}
																				target="_blank"
																				rel="noreferrer"
																			>
																				{column}
																			</a>
																		</td>
																	);
																}

																return (
																	<td key={`seal_search_data_${ colKey }`}>
																		{column}
																	</td>
																);
															} )}
														</tr>
													);
												} )}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{refinementState.view === View.GRID && (
								<div className="c-bearings-grid">
									<ul className="c-bearings-grid__list">
										{refinementState.displayedData.map( ( row, rowIndex ) => {
											const slicedRow = [ ...row ];
											const slicedColKeys = [ ...columnKeys ];
											const rowKey = generateId(
												`${ row[ keyFieldId ] || rowIndex }`,
											);
											let detailsUrl = '';
											let drawUrl = '';
											let heading: string;

											if ( urlFieldId >= 0 && urlFieldId < columnKeys.length ) {
												slicedRow.splice( urlFieldId, 1 );
												slicedColKeys.splice( urlFieldId, 1 );
												heading = row[ urlFieldId ];

												if ( urlComposer ) {
													detailsUrl = urlComposer( row[ urlFieldId ], unit );
												}
												if ( drawUrlComposer ) {
													drawUrl = drawUrlComposer( row[ drawFieldId ]);
												}
											} else {
												slicedRow.splice( 0, 1 );
												slicedColKeys.splice( 0, 1 );
												heading = row[ 0 ];
											}

											// Split into two columns
											const columnLen = slicedRow.length;
											const middle = Math.ceil( columnLen / 2 );
											const group1 = slicedRow.slice( 0, middle );
											const group2 = slicedRow.slice( middle, columnLen );

											return (
												<li key={rowKey} className="c-bearings-grid__item">
													<div className="c-bearings-grid__head">
														<h5>{heading}</h5>

														{!! detailsUrl && (
															<a
																className="btn btn--tertiary"
																href={detailsUrl}
																target="_blank"
																rel="noreferrer"
																onClick={(e) => {
																		e.preventDefault();
																		handleProductClick( row, rowIndex, row[ urlFieldId ] )
																	}
																}
															>
																{text.viewDetails}
															</a>
														)}
													</div>

													<div className="row">
														<div className="col-md-4">
															<table>
																<tbody>
																	{group1.map( ( column, index ) => {
																		const colKey = generateId(
																			slicedColKeys[ index ],
																		);

																		if (drawUrl && colKey == 'drawingnum') {
																			return (
																				<tr key={colKey}>
																					<th scope="row">
																						{columnLabels[ slicedColKeys[ index ] ]}
																					</th>
																					<td><a
																						className="btn btn--tertiary"
																						href={drawUrl}
																						target="_blank"
																						rel="noreferrer"
																					>
																						{column}
																					</a></td>
																				</tr>
																			);
																		}

																		return (
																			<tr key={colKey}>
																				<th scope="row">
																					{columnLabels[ slicedColKeys[ index ] ]}
																				</th>
																				<td>{column}</td>
																			</tr>
																		);
																	} )}
																</tbody>
															</table>
														</div>

														<div className="offset-md-1 col-md-4">
															<table>
																<tbody>
																	{group2.map( ( column, index ) => {
																		const colKey = generateId(
																			slicedColKeys[ middle + index ],
																		);

																		return (
																			<tr key={colKey}>
																				<th scope="row">
																					{
																						columnLabels[
																							slicedColKeys[ middle + index ]
																						]
																					}
																				</th>
																				<td>{column}</td>
																			</tr>
																		);
																	} )}
																</tbody>
															</table>
														</div>
													</div>
												</li>
											);
										} )}
									</ul>
								</div>
							)}

							<SearchPagination
								total={refinementState.totalPages}
								current={refinementState.currentPage}
								onNavigate={( page: number ) =>
									dispatch( { type: 'change_page', payload: page } )
								}
							/>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default SealSearchResults;
